import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { U2000ApiValueToDisplayYmd } from '../u2000Core/tools/U2000DateTimeHelpers';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3146';

type LogicalUnitEndpointsType = {
  INITLU: string;
  PREVIEW: string;
  CLOSE: string;
  SAVE_INACTIVE_DAYS_DEFAULT: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  PREVIEW: `${LOGICALUNIT_URL}/preview`,
  CLOSE: `${LOGICALUNIT_URL}/close`,
  SAVE_INACTIVE_DAYS_DEFAULT: `${LOGICALUNIT_URL}/saveInactiveDaysDefault`,
};

export const U3146_DEFAULT_INACTIVE_DAYS = 90;
export const U3146_MIN_INACTIVE_DAYS = 1;
export const U3146_MAX_INACTIVE_DAYS = 999;

export type U3146PreviewNotEligibleReason =
  | 'recent_project_creation'
  | 'recent_billable_tec'
  | 'already_closed'
  | 'not_found'
  | 'deleted';

export type U3146InitLUPersonalization = {
  inactiveDaysDefault?: number;
};

export interface U3146InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  personalization?: U3146InitLUPersonalization;
}

export type U3146PreviewCustomer = {
  id: string;
  customerName: string;
};

export type U3146OpenStandardInvoice = {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDisplayName: string;
  status: string;
  totalAmount: number;
  balanceDue: number;
};

export type U3146PreviewLineEligible = {
  projectId: string;
  projetNumber: string;
  projetName: string;
  createdAt?: string | null;
  customer: U3146PreviewCustomer;
  lastBillableTecDate: string;
  daysSinceLastBillableTec: number;
  status: 'eligible';
  uninvoicedTecLineCount: number;
  uninvoicedTecAmountTec: number;
  willCreateWriteOff: boolean;
  openStandardInvoices: U3146OpenStandardInvoice[];
  openStandardInvoiceCount: number;
};

export type U3146PreviewLineNotEligible = {
  projectId: string;
  projetNumber: string;
  projetName: string;
  createdAt?: string | null;
  status: 'not_eligible';
  reason: U3146PreviewNotEligibleReason;
  customer?: U3146PreviewCustomer | null;
  lastBillableTecDate?: string;
  daysSinceLastBillableTec?: number;
  uninvoicedTecLineCount?: number;
  uninvoicedTecAmountTec?: number;
};

export type U3146PreviewLine = U3146PreviewLineEligible | U3146PreviewLineNotEligible;

export type U3146PreviewRequest = {
  inactiveDays: number;
};

export type U3146PreviewResult = {
  inactiveDaysUsed: number;
  asOfDate: string;
  lines: U3146PreviewLine[];
};

export type U3146CloseRequest = {
  inactiveDays: number;
  saveInactiveDaysAsDefault: boolean;
  projectIds: string[];
};

export type U3146ClosedStandardInvoice = {
  invoiceId: string;
  invoiceNumber: string;
  previousStatus: string;
};

export type U3146CloseLineClosed = {
  projectId: string;
  projetNumber: string;
  status: 'closed';
  tecLinesWrittenOff: number;
  writeOffInvoiceId?: string;
  writeOffInvoiceNumber?: string;
  standardInvoicesClosed: U3146ClosedStandardInvoice[];
};

export type U3146CloseLineError = {
  projectId: string;
  projetNumber: string;
  status: 'error';
  errorCode?: string;
  message?: string;
};

export type U3146CloseLineResult = U3146CloseLineClosed | U3146CloseLineError;

export type U3146CloseResult = {
  inactiveDaysUsed: number;
  asOfDate: string;
  lines: U3146CloseLineResult[];
};


export function u3146PreviewEligibleProjectIds(lines: U3146PreviewLine[]): string[] {
  return lines.filter(u3146PreviewLineIsEligible).map((line) => line.projectId);
}

export function u3146PreviewLineIsEligible(line: U3146PreviewLine): line is U3146PreviewLineEligible {
  return line.status === 'eligible';
}

/** Colonnes date / jours / TEC non fact. : éligible ou TEC facturable récente. */
export function u3146PreviewLineShowsTecColumns(line: U3146PreviewLine): boolean {
  return line.status === 'eligible' || (line.status === 'not_eligible' && line.reason === 'recent_billable_tec');
}

/** Libellé client grille : éligible ou TEC facturable récente si l'API envoie un client. */
export function u3146PreviewLineCustomerDisplayName(line: U3146PreviewLine): string {
  if (line.status === 'eligible') {
    return line.customer?.customerName?.trim() || '—';
  }
  if (line.status === 'not_eligible' && line.reason === 'recent_billable_tec') {
    return line.customer?.customerName?.trim() || '—';
  }
  return '—';
}

export function u3146CloseLineIsClosed(line: U3146CloseLineResult): line is U3146CloseLineClosed {
  return line.status === 'closed';
}

export function u3146PreviewReasonLabelKey(reason: U3146PreviewNotEligibleReason): string {
  return `reason_${reason}`;
}

export function u3146NormalizeInactiveDaysDefault(personalization?: U3146InitLUPersonalization | null): number {
  const n = personalization?.inactiveDaysDefault;
  if (typeof n === 'number' && Number.isFinite(n) && n >= U3146_MIN_INACTIVE_DAYS) {
    return Math.min(U3146_MAX_INACTIVE_DAYS, Math.floor(n));
  }
  return U3146_DEFAULT_INACTIVE_DAYS;
}

export function u3146IsValidInactiveDays(value: number): boolean {
  return Number.isInteger(value) && value >= U3146_MIN_INACTIVE_DAYS && value <= U3146_MAX_INACTIVE_DAYS;
}

function extractResult<T>(data: unknown): T | undefined {
  const root = data as { result?: T };
  return root?.result;
}

function u3146NumFromUnknown(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeU3146OpenStandardInvoice(raw: unknown): U3146OpenStandardInvoice {
  const o = (raw ?? {}) as Record<string, unknown>;
  const status = String(o.status ?? '');
  const totalAmount = u3146NumFromUnknown(o.totalAmount ?? o.total_amount) ?? 0;
  const balanceDueFromApi = u3146NumFromUnknown(o.balanceDue ?? o.balance_due);
  return {
    invoiceId: String(o.invoiceId ?? o.invoice_id ?? ''),
    invoiceNumber: String(o.invoiceNumber ?? o.invoice_number ?? ''),
    invoiceDisplayName: String(
      o.invoiceDisplayName ?? o.invoice_display_name ?? o.invoiceNumber ?? o.invoice_number ?? '',
    ),
    status,
    totalAmount,
    balanceDue:
      balanceDueFromApi !== undefined
        ? balanceDueFromApi
        : status === 'RELEASE'
          ? totalAmount
          : 0,
  };
}

/** Solde affichable : API ou repli total TTC pour RELEASE sans paiements enregistrés. */
export function u3146OpenStandardInvoiceBalanceDue(inv: U3146OpenStandardInvoice): number {
  if (inv.balanceDue != null && Number.isFinite(inv.balanceDue)) {
    return inv.balanceDue;
  }
  if (inv.status === 'RELEASE' && Number.isFinite(inv.totalAmount)) {
    return inv.totalAmount;
  }
  return 0;
}

function u3146NormalizeCreatedAt(raw: unknown): string | null {
  return U2000ApiValueToDisplayYmd(raw);
}

function u3146NormalizePreviewCustomer(raw: unknown): U3146PreviewCustomer | null {
  const customerRaw = raw as Record<string, unknown> | null | undefined;
  if (!customerRaw?.id) {
    return null;
  }
  const name = String(customerRaw.customerName ?? customerRaw.customer_name ?? '').trim();
  return {
    id: String(customerRaw.id),
    customerName: name,
  };
}

function normalizeU3146PreviewLine(raw: unknown): U3146PreviewLine {
  const o = (raw ?? {}) as Record<string, unknown>;
  const createdAt = u3146NormalizeCreatedAt(o.createdAt ?? o.created_at);
  if (o.status === 'not_eligible') {
    const reason = o.reason as U3146PreviewLineNotEligible['reason'];
    const line: U3146PreviewLineNotEligible = {
      projectId: String(o.projectId ?? o.project_id ?? ''),
      projetNumber: String(o.projetNumber ?? o.projet_number ?? ''),
      projetName: String(o.projetName ?? o.projet_name ?? ''),
      createdAt,
      status: 'not_eligible',
      reason,
      customer: u3146NormalizePreviewCustomer(o.customer),
    };
    if (reason === 'recent_billable_tec') {
      const lastBillableTecDate = String(o.lastBillableTecDate ?? o.last_billable_tec_date ?? '').trim();
      if (lastBillableTecDate) {
        line.lastBillableTecDate = lastBillableTecDate;
      }
      const days = u3146NumFromUnknown(o.daysSinceLastBillableTec ?? o.days_since_last_billable_tec);
      if (days != null) {
        line.daysSinceLastBillableTec = days;
      }
      const count = u3146NumFromUnknown(o.uninvoicedTecLineCount ?? o.uninvoiced_tec_line_count);
      if (count != null) {
        line.uninvoicedTecLineCount = count;
      }
      const amount = u3146NumFromUnknown(o.uninvoicedTecAmountTec ?? o.uninvoiced_tec_amount_tec);
      if (amount != null) {
        line.uninvoicedTecAmountTec = amount;
      }
    }
    return line;
  }
  const customerRaw = o.customer as Record<string, unknown> | null | undefined;
  return {
    projectId: String(o.projectId ?? o.project_id ?? ''),
    projetNumber: String(o.projetNumber ?? o.projet_number ?? ''),
    projetName: String(o.projetName ?? o.projet_name ?? ''),
    createdAt,
    customer: customerRaw
      ? {
          id: String(customerRaw.id ?? ''),
          customerName: String(customerRaw.customerName ?? customerRaw.customer_name ?? ''),
        }
      : { id: '', customerName: '' },
    lastBillableTecDate: String(o.lastBillableTecDate ?? o.last_billable_tec_date ?? ''),
    daysSinceLastBillableTec: u3146NumFromUnknown(o.daysSinceLastBillableTec ?? o.days_since_last_billable_tec) ?? 0,
    status: 'eligible',
    uninvoicedTecLineCount: u3146NumFromUnknown(o.uninvoicedTecLineCount ?? o.uninvoiced_tec_line_count) ?? 0,
    uninvoicedTecAmountTec: u3146NumFromUnknown(o.uninvoicedTecAmountTec ?? o.uninvoiced_tec_amount_tec) ?? 0,
    willCreateWriteOff: Boolean(o.willCreateWriteOff ?? o.will_create_write_off),
    openStandardInvoices: Array.isArray(o.openStandardInvoices)
      ? o.openStandardInvoices.map(normalizeU3146OpenStandardInvoice)
      : [],
    openStandardInvoiceCount: u3146NumFromUnknown(o.openStandardInvoiceCount ?? o.open_standard_invoice_count) ?? 0,
  };
}

export function parseU3146PreviewResult(data: unknown): U3146PreviewResult | null {
  const result = extractResult<Record<string, unknown>>(data);
  if (!result || !Array.isArray(result.lines)) return null;
  return {
    inactiveDaysUsed: u3146NumFromUnknown(result.inactiveDaysUsed ?? result.inactive_days_used) ?? 0,
    asOfDate: String(result.asOfDate ?? result.as_of_date ?? ''),
    lines: result.lines.map(normalizeU3146PreviewLine),
  };
}

export function parseU3146CloseResult(data: unknown): U3146CloseResult | null {
  const result = extractResult<U3146CloseResult>(data);
  if (!result || !Array.isArray(result.lines)) return null;
  return result;
}

export function parseU3146InitLU(data: unknown): U3146InitLU {
  const result = extractResult<U3146InitLU>(data) ?? (data as U3146InitLU);
  return {
    launchSearch: result?.launchSearch ?? false,
    hideSearch: result?.hideSearch ?? true,
    nbRecordPerPage: result?.nbRecordPerPage ?? 25,
    personalization: result?.personalization,
  };
}

export function luHandleAppiError(_t: TranslateFn, NS: string, err: { errorCode?: string; description?: string }, errorResult: U2000ApiErrorResult): void {
  const code = err.errorCode;
  if (code?.startsWith('BU3146')) {
    errorResult.genericErrors.push({
      title: _t('errorTitle', { ns: NS }),
      text: _t(`err${code}`, { ns: NS, defaultValue: err.description ?? code }),
    });
  }
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU, { params: { b: U2000GenerateUrlUUID() } });
};

export const doPreview = (axios: AxiosInstance, body: U3146PreviewRequest) => {
  return axios.post(LogicalUnitEndpoints.PREVIEW, body);
};

export const doClose = (axios: AxiosInstance, body: U3146CloseRequest) => {
  return axios.post(LogicalUnitEndpoints.CLOSE, body);
};

export const doSaveInactiveDaysDefault = (axios: AxiosInstance, inactiveDays: number) => {
  return axios.post(LogicalUnitEndpoints.SAVE_INACTIVE_DAYS_DEFAULT, { inactiveDays });
};
